import React, { useCallback, useRef, useState } from 'react'

import {
  ResizeHandle,
  StyledTh,
} from 'src/features/charts-v2/components/ResizableHeaderCell/resizable-header-cell.styles'

const MIN_COLUMN_WIDTH = 60

export interface ResizableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  width?: number
  onResize?: (width: number) => void
}

/**
 * antd Table `components.header.cell` override that adds a draggable resize
 * handle to the column's right edge. Columns that don't pass `width` /
 * `onResize` (antd's own selection/expand columns) render a plain `<th>`.
 */
export const ResizableHeaderCell: React.FC<ResizableHeaderCellProps> = ({
  width,
  onResize,
  children,
  ...restProps
}) => {
  const [isResizing, setIsResizing] = useState(false)
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(
    null,
  )

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const dragState = dragStateRef.current
      if (!dragState || !onResize) return
      const delta = event.clientX - dragState.startX
      onResize(Math.max(MIN_COLUMN_WIDTH, dragState.startWidth + delta))
    },
    [onResize],
  )

  const handlePointerUp = useCallback(() => {
    dragStateRef.current = null
    setIsResizing(false)
    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', handlePointerUp)
  }, [handlePointerMove])

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLSpanElement>) => {
      // Stop the drag from reaching the column header's onClick, which
      // toggles antd's sorter — without this every resize also flips sort.
      event.preventDefault()
      event.stopPropagation()
      dragStateRef.current = { startX: event.clientX, startWidth: width ?? 0 }
      setIsResizing(true)
      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)
    },
    [handlePointerMove, handlePointerUp, width],
  )

  if (width == null || !onResize) {
    return <th {...restProps}>{children}</th>
  }

  return (
    <StyledTh {...restProps} $width={width}>
      {children}
      <ResizeHandle
        className={isResizing ? 'resizing' : undefined}
        data-testid="column-resize-handle"
        onPointerDown={handlePointerDown}
        onClick={(event) => event.stopPropagation()}
      />
    </StyledTh>
  )
}
